/**
 * # Auto-Generated Email Flag Dialog
 * 
 * Dialog som vises når e-post er flagget som auto-generert
 * Lar bruker velge: Always (add to ignored), Yes (ignore this), eller X (keep)
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Bot, X, CheckCircle, AlertCircle } from 'lucide-react';
import { logDev } from '@/shared/lib/log-dev';

interface AutoGeneratedFlagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  emailId: string;
  fromEmail: string;
  fromName: string | null;
  subject: string | null;
  detection?: {
    isAutomatic: boolean;
    confidence: number;
    category: string;
    reasons: string[];
    riskLevel: 'safe' | 'review' | 'keep';
  } | null;
  score?: number | null; // 0-100 score from database
  category?: string | null; // Category from database
  onAction?: (action: 'always_ignore' | 'ignore' | 'keep') => void;
}

export function AutoGeneratedFlagDialog({
  open,
  onOpenChange,
  emailId,
  fromEmail,
  fromName,
  subject,
  detection,
  score,
  category,
  onAction,
}: AutoGeneratedFlagDialogProps) {
  // Use score from database if available, otherwise use detection confidence
  const displayScore = score !== null && score !== undefined 
    ? score 
    : detection 
      ? Math.round(detection.confidence * 100) 
      : 0;
  
  const displayCategory = category || detection?.category || 'unknown';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAction = async (action: 'always_ignore' | 'ignore' | 'keep') => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/supplier-emails/${emailId}/flag-auto-generated`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });

      if (!response.ok) {
        throw new Error('Failed to apply action');
      }

      const data = await response.json();

      if (data.success) {
        logDev.info('[AutoGeneratedFlagDialog] Action applied', { emailId, action });
        
        // Notify parent
        if (onAction) {
          onAction(action);
        }

        // Close dialog
        onOpenChange(false);
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (err: any) {
      logDev.error('[AutoGeneratedFlagDialog] Error', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'cicd':
        return 'bg-blue-500/10 text-blue-500';
      case 'no-reply':
        return 'bg-red-500/10 text-red-500';
      case 'notification':
        return 'bg-yellow-500/10 text-yellow-500';
      case 'marketing':
        return 'bg-purple-500/10 text-purple-500';
      case 'transactional':
        return 'bg-green-500/10 text-green-500';
      default:
        return 'bg-gray-500/10 text-gray-500';
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      'cicd': 'CI/CD',
      'no-reply': 'No-Reply',
      'notification': 'Notification',
      'marketing': 'Marketing',
      'transactional': 'Transactional',
      'generated': 'Generated',
    };
    return labels[category] || category;
  };

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'safe':
        return <AlertCircle className="h-4 w-4 text-blue-500" />;
      case 'review':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-blue-500" />
            Flagged as Auto-Generated
          </DialogTitle>
          <DialogDescription>
            This email was detected as automatically generated. What would you like to do?
          </DialogDescription>
        </DialogHeader>

        {/* Email Info */}
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-muted-foreground">From</p>
            <p className="text-sm">{fromName || fromEmail}</p>
            <p className="text-xs text-muted-foreground">{fromEmail}</p>
          </div>

          {subject && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">Subject</p>
              <p className="text-sm">{subject}</p>
            </div>
          )}

          {/* Detection Details */}
          <div className="flex items-center gap-2 pt-2 border-t flex-wrap">
            <Badge className={getCategoryColor(displayCategory)}>
              {getCategoryLabel(displayCategory)}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {displayScore}% probability
            </Badge>
            {displayScore >= 70 && (
              <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                Likely auto-generated
              </span>
            )}
            {displayScore >= 30 && displayScore < 70 && (
              <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                Uncertain
              </span>
            )}
            {displayScore < 30 && (
              <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                Likely human
              </span>
            )}
          </div>

          {/* Reasons - only show if detection object exists */}
          {detection && detection.reasons.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Detection reasons:</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                {detection.reasons.slice(0, 3).map((reason, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-blue-500 mt-0.5">•</span>
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md">
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {/* Always Button - Add to ignored_senders */}
          <Button
            variant="destructive"
            onClick={() => handleAction('always_ignore')}
            disabled={loading}
            className="flex-1"
          >
            Always
          </Button>

          {/* Yes Button - Ignore this email */}
          <Button
            variant="default"
            onClick={() => handleAction('ignore')}
            disabled={loading}
            className="flex-1"
          >
            Yes
          </Button>

          {/* X Button - Keep email */}
          <Button
            variant="outline"
            onClick={() => handleAction('keep')}
            disabled={loading}
            className="flex-1"
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogFooter>

        {/* Action Explanations */}
        <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
          <p><strong>Always:</strong> Add sender to ignored list (future emails auto-ignored)</p>
          <p><strong>Yes:</strong> Ignore this email only</p>
          <p><strong>X:</strong> Keep email (don't ignore)</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

